/**
 * Resultados de busqueda (BUS-01, BUS-06).
 *
 * Lo importante de esta pantalla no es cuando encuentra: es cuando no
 * encuentra. Una busqueda sin resultado no puede terminar en "0 productos" —
 * ahi se pierde la venta. Termina en WhatsApp, con el termino ya escrito, que
 * es donde hoy se resuelve.
 *
 * No se indexa: son URL infinitas generadas por el visitante y no aportan nada
 * al catalogo en Google.
 */
import { useSearchParams } from 'react-router-dom';

import type { FiltrosBusqueda as Filtros } from '@elcarpintero/compartido';

import { ListadoFiltrable } from '../componentes/catalogo/ListadoFiltrable';
import { Icono } from '../componentes/comunes/Icono';
import { useListadoInfinito } from '../hooks/use-listado-infinito';
import { useUltimoDe } from '../hooks/use-ultimo-de';
import { Seo } from '../seo/Seo';
import { buscar } from '../servicios/busqueda.servicio';
import { registrarClicWhatsapp } from '../servicios/metricas.servicio';
import { pedirBusquedaPorFoto } from '../utilidades/busqueda-foto-atajo';
import { claveFiltros, escribirFiltros, leerFiltros } from '../utilidades/filtros-busqueda';
import { conteoProductos } from '../utilidades/formato';
import { rutas } from '../utilidades/rutas';
import { whatsappBusqueda } from '../utilidades/whatsapp';

export function Busqueda() {
  const [parametros, setParametros] = useSearchParams();
  const termino = parametros.get('termino') ?? '';
  const filtros = leerFiltros(parametros);

  // Scroll infinito: las paginas siguientes se suman al bajar, hasta el total.
  const listado = useListadoInfinito(
    JSON.stringify([termino, claveFiltros(filtros)]),
    (pagina, senal) => buscar(termino, { pagina, filtros, senal }),
  );
  const { datos } = listado;
  const facetas = useUltimoDe(termino, datos)?.facetas ?? null;

  const filtrar = (siguientes: Filtros) => {
    setParametros(escribirFiltros(siguientes, termino));
  };

  return (
    <>
      <Seo
        titulo={termino.length > 0 ? `Resultados para "${termino}"` : 'Buscar en el catálogo'}
        descripcion={`Resultados de búsqueda en el catálogo de El Carpintero para "${termino}".`}
        ruta={rutas.busqueda(termino)}
        sinIndexar
      />

      <div className="contenedor pagina">
        <header className="encabezado-pag">
          <div>
            <h1>
              {termino.length > 0 ? <>Resultados para «{termino}»</> : 'Buscar en el catálogo'}
            </h1>
            {datos !== null && !datos.sinResultados ? (
              <p className="sub">{conteoProductos(datos.total, datos.totalSinFiltros)}</p>
            ) : null}
          </div>
        </header>

        {/*
          Cuando lo escrito no encontraba nada y el buscador corrigio un error de
          tipeo, la pantalla lo DICE. Sin este aviso, quien escribio "bisgara" y
          ve bisagras no sabe si el sitio le entendio o le esta mostrando
          cualquier cosa — y no hay forma de que lo averigue.

          El texto dice "buscaste X" y no "no encontramos X" porque lo escrito
          puede haber encontrado algo: se corrige tambien cuando encontro muy
          poco, y ahi "no encontramos" seria falso.

          No se ofrece "buscar «bisgara» de todas formas", que es lo que hace
          Google: ahi ese enlace lleva a resultados peores, pero acá llevaria
          exactamente a cero, que fue lo que paso antes de corregir.
        */}
        {datos !== null && datos.terminoCorregido !== null ? (
          <p className="aviso-correccion">
            <Icono nombre="info" />
            <span>
              Buscaste «{datos.termino}». Te mostramos resultados para{' '}
              <strong>«{datos.terminoCorregido}»</strong>.
            </span>
          </p>
        ) : null}

        <ListadoFiltrable
          listado={listado}
          facetas={facetas}
          filtros={filtros}
          onFiltrar={filtrar}
          titulo={`Resultados para ${termino}`}
          que="los resultados"
          donde={`para «${termino}»`}
          vacio={
            <section className="rescate" aria-labelledby="sin-resultados">
              <h2 id="sin-resultados">
                <Icono nombre="lupaVacia" />
                No encontramos «{termino}» en el catálogo en línea
              </h2>
              <p>
                Puede que lo tengamos en tienda sin publicar todavía, o que se llame distinto en el
                rubro. Si tienes la pieza a mano, buscala por foto; si no, escribenos y te
                confirmamos al tiro: el mensaje ya va con lo que buscaste.
              </p>
              <p className="acc">
                <a
                  className="btn btn-wa"
                  href={whatsappBusqueda(termino)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => registrarClicWhatsapp({ origen: 'busqueda', termino })}
                >
                  <Icono nombre="wa" />
                  Preguntar por WhatsApp
                </a>
                {/*
                  Que se llame distinto en el rubro es exactamente el caso que la
                  busqueda por foto resuelve, asi que el camino corto va aca.
                */}
                <button type="button" className="btn" onClick={pedirBusquedaPorFoto}>
                  <Icono nombre="camara" />
                  Buscar por foto
                </button>
              </p>
            </section>
          }
        />
      </div>
    </>
  );
}
